import React, { useState, useRef, useEffect } from "react";
import { DownOutlined, CheckOutlined, SearchOutlined, LoadingOutlined } from "@ant-design/icons";

const FilterDropdown = ({
  label,
  options,
  value,
  onChange,
  multiSelect = false,
  searchable  = false,
  onSearch    = null,
}) => {
  const [open,            setOpen]            = useState(false);
  const [innerSearch,     setInnerSearch]     = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching,       setSearching]       = useState(false);
  const [pending,         setPending]         = useState([]);
  const [dropPos,         setDropPos]         = useState({ top: 0, left: 0 });
  const ref         = useRef(null);
  const btnRef      = useRef(null);
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setInnerSearch(val);
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setSearching(false);
      if (onSearch) onSearch(val);
    }, 300);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setInnerSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (!open) {
      if (multiSelect) setPending(value || []);
      const rect = btnRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 6, left: rect.left });
    }
    setOpen((o) => !o);
    setInnerSearch("");
  };

  const visibleOptions =
    onSearch
      ? options
      : searchable && debouncedSearch.trim()
        ? options.filter((o) => o.toLowerCase().includes(debouncedSearch.toLowerCase()))
        : options;

  const selectSingle = (opt) => {
    onChange(value === opt ? null : opt);
    setOpen(false);
  };

  const togglePending = (opt) =>
    setPending((prev) =>
      prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]
    );

  const applyMulti = () => {
    onChange(pending.length ? pending : null);
    setOpen(false);
    setInnerSearch("");
  };

  const isActive = multiSelect ? value?.length > 0 : !!value;

  const btnLabel = multiSelect
    ? value?.length
      ? <>{label} <span className="sjm-filter-count">{value.length}</span></>
      : label
    : value || label;

  return (
    <div className="sjm-filter-wrap" ref={ref}>
      <button
        ref={btnRef}
        type="button"
        className={`sjm-filter-btn${isActive ? " sjm-filter-active" : ""}`}
        onClick={handleToggle}
      >
        {btnLabel}
        <DownOutlined className={`sjm-filter-arrow${open ? " sjm-arrow-up" : ""}`} />
      </button>

      {open && (
        <div
          className={`sjm-dropdown${multiSelect ? " sjm-dropdown-multi" : ""}`}
          style={{ position: "fixed", top: dropPos.top, left: dropPos.left }}
        >
          {searchable && (
            <div className="sjm-dd-search-wrap">
              <SearchOutlined className="sjm-dd-search-icon" />
              <input
                className="sjm-dd-search-input"
                placeholder="Search"
                value={innerSearch}
                onChange={handleSearchChange}
                autoFocus
              />
              {searching && <LoadingOutlined className="sjm-dd-search-loader" />}
            </div>
          )}

          <div className="sjm-dd-list">
            {visibleOptions.length === 0 ? (
              <div className="sjm-dd-no-results">No results</div>
            ) : multiSelect ? (
              visibleOptions.map((opt) => {
                const checked = pending.includes(opt);
                return (
                  <div
                    key={opt}
                    className={`sjm-dd-item sjm-dd-item-check${checked ? " sjm-dd-item-checked" : ""}`}
                    onClick={() => togglePending(opt)}
                  >
                    <div className={`sjm-checkbox${checked ? " sjm-checkbox-on" : ""}`}>
                      {checked && <CheckOutlined className="sjm-checkbox-tick" />}
                    </div>
                    <span>{opt}</span>
                  </div>
                );
              })
            ) : (
              visibleOptions.map((opt) => (
                <div
                  key={opt}
                  className={`sjm-dd-item${value === opt ? " sjm-dd-item-selected" : ""}`}
                  onClick={() => selectSingle(opt)}
                >
                  <span>{opt}</span>
                  {value === opt && <CheckOutlined className="sjm-dropdown-check" />}
                </div>
              ))
            )}
          </div>

          {multiSelect && (
            <div className="sjm-dd-footer">
              <button className="sjm-dd-apply-btn" type="button" onClick={applyMulti}>
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
